/**
 * Canvas Sync Utility - Makes canvas the primary output surface for investigation results
 *
 * When auto-investigation completes, this syncs findings to the graph canvas:
 * - Exchange detection → Node styling updates
 * - Backward trace → Gold Path auto-renders
 * - Signal ESCALATE → Node pulses red
 * - Velocity classification → Node badge overlay
 * - High-risk counterparties → Highlighting
 */

import type { Core as CytoscapeCore } from 'cytoscape';
import type { GraphFilters } from '../components/workbench/GraphFilterBar';
import { useGraphStore } from '../stores/useGraphStore';
import { CanvasNodeIntent, CanvasEdgeIntent } from '../types/canvasIntent';
import { pickHigherRole } from '../constants/placementRoles';
import { VISIBILITY } from '../constants/visibility';
import { applyCanvasIntent } from './applyCanvasIntent';
import { isTier1EntityType } from './canvasAssurance';
import { formatConsolidatedLabel } from './edgeConsolidation';
import { cleanTokenSymbol } from './safeStr';
import { addressService } from '../services/api';
import { detectChain, detectChainStrict } from './chainDetect';
// Static import (not require()) — require is a Node global that only type-checks
// when @types/node leaks in via test files. These recorders are no-ops unless
// ?pipelineDebug=true, so always-bundling them is cheap.
import { recordEdgeValueChange, recordGraphMutation } from './pipelineRecorder';
import { isExchangeByName } from '../constants/entityKeywords';

/** Placeholder / non-API chain labels that must not be sent to `/addresses/{chain}/…`. */
const PLACEHOLDER_CHAINS = new Set(['UNKNOWN', 'N/A', 'NONE', 'NULL', '-', '—', '?']);

/**
 * True when `chain` is missing or a non-actionable placeholder (e.g. "Unknown"
 * stamped by OSINT / CSV when the format was ambiguous).
 */
export function isPlaceholderChain(chain?: string | null): boolean {
  if (!chain || !String(chain).trim()) return true;
  return PLACEHOLDER_CHAINS.has(String(chain).trim().toUpperCase());
}

/**
 * Normalize chain aliases to canonical ticker symbols.
 * Handles 'Chain.TRX' stringification and common aliases.
 */
export function normalizeChain(rawChain?: string | null): string {
  if (!rawChain) return 'ETH';
  let value = rawChain.trim().toUpperCase();
  
  // Handle stringified Enum members like "Chain.TRX" or "CHAIN.BTC"
  if (value.startsWith('CHAIN.')) {
    value = value.replace('CHAIN.', '');
  }

  const aliases: Record<string, string> = {
    ETHEREUM: 'ETH',
    ETH: 'ETH',
    ERC20: 'ETH',
    TRON: 'TRX',
    TRX: 'TRX',
    BITCOIN: 'BTC',
    BTC: 'BTC',
    POLYGON: 'MATIC',
    MATIC: 'MATIC',
    BINANCE: 'BNB',
    BSC: 'BNB',
    BNB: 'BNB',
    ARBITRUM: 'ARB',
    ARB: 'ARB',
    OPTIMISM: 'OP',
    OP: 'OP',
    BASE: 'BASE',
    AVAX: 'AVAX',
    AVALANCHE: 'AVAX',
    LINEA: 'LINEA',
    BLAST: 'BLAST',
    SCROLL: 'SCROLL',
    GNOSIS: 'GNOSIS',
    XDAI: 'GNOSIS',
    CELO: 'CELO',
    MANTLE: 'MANTLE',
    MOONBEAM: 'MOONBEAM',
    XRP: 'XRP',
    TON: 'TON',
    SOLANA: 'SOL',
    SOL: 'SOL',
  };

  return aliases[value] || value;
}

/**
 * Pick a chain that is safe to call address APIs with.
 * Skips placeholder labels ("Unknown"/"UNKNOWN") and falls back to format
 * detection from the address, then ETH.
 */
export function resolveUsableChain(
  address?: string | null,
  ...candidates: Array<string | null | undefined>
): string {
  for (const c of candidates) {
    if (isPlaceholderChain(c)) continue;
    const normalized = normalizeChain(c);
    if (!isPlaceholderChain(normalized)) return normalized;
  }
  if (address) {
    const strict = detectChainStrict(address);
    if (strict && !isPlaceholderChain(strict)) return normalizeChain(strict);
    return detectChain(address);
  }
  return 'ETH';
}

/** Check if a node ID is a BTC TX-node (diamond) rather than an address. */
export function isTxNode(id: string): boolean {
  return !!id && id.startsWith('tx:');
}

/**
 * Same 0x hex cannot exist twice (id is the bare address).
 * When the user picks a different EVM chain, rewrite that node's `data.chain`.
 */
export function retargetAddressChain<T extends { data?: Record<string, unknown> }>(
  elements: T[],
  address: string,
  chain: string,
): T[] {
  const want = (address || '').toLowerCase();
  const nextChain = (chain || '').toUpperCase();
  if (!want || !nextChain) return elements;
  return elements.map((el) => {
    const data = el.data;
    if (!data || data.source || data.target) return el;
    const id = typeof data.id === 'string' ? data.id : '';
    if (id.toLowerCase() !== want) return el;
    return { ...el, data: { ...data, chain: nextChain } };
  });
}

/** Normalize address for canvas node ID matching. EVM -> lowercase, others preserved. TX-nodes returned as-is. */
export function normalizeCanvasAddr(address: string): string {
  if (!address) return address;
  if (isTxNode(address)) return address;  // TX-nodes are not addresses
  if (address.startsWith('0x') || address.startsWith('0X')) return address.toLowerCase();
  return address;
}

export interface FilterPreset {
  label: string;
  filters: Partial<GraphFilters>;
}

/**
 * Build a filter preset based on investigation findings.
 * Returns null if no actionable preset applies.
 */
export function buildInvestigationPreset(
  results: InvestigationResults
): FilterPreset | null {
  const hasGoldPath = results.backward_trace?.found && (results.backward_trace.paths?.length ?? 0) > 0;

  // Backend returns counterparties wrapped in {counterparties: [...]} OR as flat array
  const cpList = Array.isArray(results.counterparty_triage)
    ? results.counterparty_triage
    : (results.counterparty_triage as any)?.counterparties ?? [];
  // Backend risk tiers: "critical", "severe", "high", "medium", "low"
  const hasHighRiskCPs = cpList.some((cp: any) =>
    cp.risk_tier === 'high' || cp.risk_tier === 'severe' || cp.risk_tier === 'critical'
  );

  // Signal assessment escalation
  const escalation = results.signal_assessment?.recommendation;
  const isEscalate = escalation === 'ESCALATE';

  if (isEscalate && hasGoldPath) {
    return {
      label: 'Escalation Focus',
      filters: { showOnlyHighRisk: true },
    };
  }
  if (hasGoldPath && hasHighRiskCPs) {
    return {
      label: 'Investigation Focus',
      filters: { showOnlyHighRisk: true },
    };
  }
  if (isEscalate || hasHighRiskCPs) {
    return {
      label: 'High-Risk Focus',
      filters: { showOnlyHighRisk: true },
    };
  }
  return null;
}

export interface InvestigationResults {
  address: string;
  exchange_detection?: {
    is_exchange: boolean;
    exchange_name?: string;
    exchange_type?: string;
    confidence?: number;
  };
  velocity_profile?: {
    classification: string; // Relay, Accumulator, Distributor, Dormant, Mixed
    fund_through_rate?: number;
  };
  signal_assessment?: {
    recommendation: string; // ESCALATE, INVESTIGATE, MONITOR, DEPRIORITIZE
    escalation_recommendation?: string; // alias used by signal_assembler
    families_triggered?: number;
    families_tested?: number;
  };
  backward_trace?: {
    found: boolean;
    paths?: Array<{
      path: string[];
      hops: number;
      target_entity?: { name: string; type: string; address: string };
      hop_details?: Array<{
        address: string;
        classification: string;
        fund_through_rate?: number;
        fan_in?: number;
        fan_out?: number;
        dwell_time_seconds?: number | null;
      }>;
      edges?: any[];
    }>;
    source_entities?: Array<{ name: string; type: string; address: string }>;
    hop_intelligence?: Array<{
      address: string;
      classification: string;
      fund_through_rate?: number;
      fan_in?: number;
      fan_out?: number;
    }>;
  };
  forward_trace?: {
    found: boolean;
    paths?: Array<{
      path: string[];
      hops: number;
      target_entity?: { name: string; type: string; address: string };
      hop_details?: Array<{
        address: string;
        classification: string;
        dwell_time_seconds?: number | null;
      }>;
      edges?: any[];
    }>;
    target_entities?: Array<{ name: string; type: string; address: string }>;
    hop_intelligence?: Array<{
      address: string;
      classification: string;
      fund_through_rate?: number;
      fan_in?: number;
      fan_out?: number;
    }>;
  };
  intelligence_attributions?: Array<{
    label: string;
    confidence: number;
    source: string;
  }>;
  // Backend wraps in {counterparties: [...]} but may be passed as flat array
  counterparty_triage?: Array<{
    address: string;
    risk_tier: string; // critical, severe, high, medium, low
    risk_score?: number;
    entity_name?: string;
    entity_type?: string;
    total_volume?: number;
    tx_count?: number;
    sanctioned?: boolean;
  }> | {
    counterparties: Array<{
      address: string;
      risk_tier: string;
      risk_score?: number;
      entity_name?: string;
      entity_type?: string;
      total_volume?: number;
      tx_count?: number;
      sanctioned?: boolean;
    }>;
    total_counterparties?: number;
    analysis_tx_count?: number;
  };
}

/**
 * Main sync function - Call this when auto-investigation completes
 */
export function syncInvestigationToCanvas(
  results: InvestigationResults,
  cy: CytoscapeCore,
  chain: string
): void {
  const normalizedTarget = normalizeCanvasAddr(results.address);
  console.log('🎨 Canvas Sync: Starting visual sync for', normalizedTarget);

  const targetNode = cy.getElementById(normalizedTarget);
  if (!targetNode.length) {
    console.log('⚠️ Canvas Sync: Target node not on canvas, skipping visual updates');
    return;
  }

  // 0. Primary Target → Set as Target Root
  applyCanvasIntent(targetNode, CanvasNodeIntent.TARGET_ROOT);

  // 1. Exchange Detection → Update node styling
  if (results.exchange_detection?.is_exchange) {
    targetNode.data('entity_type', 'Exchange');
    targetNode.data('entity_name', results.exchange_detection.exchange_name || 'Exchange');
    applyCanvasIntent(targetNode, CanvasNodeIntent.CRITICAL_ENDPOINT);
    console.log('✓ Canvas: Updated node as exchange:', results.exchange_detection.exchange_name);
  }

  // 2. Signal Assessment → Add escalation styling
  const recommendation = results.signal_assessment?.recommendation;
  if (recommendation === 'ESCALATE') {
    targetNode.addClass('escalate-node'); // Still keep class for pulsing effect
    targetNode.data('escalation_level', 'ESCALATE');
    console.log('✓ Canvas: Added ESCALATE styling (pulsing red border)');
  } else if (recommendation === 'INVESTIGATE') {
    targetNode.addClass('investigate-node');
    targetNode.data('escalation_level', 'INVESTIGATE');
    console.log('✓ Canvas: Added INVESTIGATE styling (amber border)');
  }

  // 3. Velocity Classification → Add badge
  if (results.velocity_profile?.classification) {
    const badge = getVelocityBadge(results.velocity_profile.classification);
    targetNode.data('velocity_badge', badge);
    targetNode.data('velocity_classification', results.velocity_profile.classification);
    console.log(`✓ Canvas: Added velocity badge ${badge} (${results.velocity_profile.classification})`);
  }

  // 4. Backward + Forward Trace → Render Gold Paths
  //
  // Both directions feed the same renderer. paths[0] gets PRIMARY_TRACE
  // (thick amber), paths[1..] get SECONDARY_TRACE (thinner amber). Before
  // 2026-05-05 we only rendered backward_trace.paths[0] — losing every
  // additional path BFS returned (v17 dropped 9 of 10 paths). forward_trace
  // wasn't rendered at all.
  //
  // Clear stale gold-path styling from any previous investigation BEFORE
  // both calls run, so the forward-trace render doesn't wipe what the
  // backward-trace render just set on shared edges.
  cy.edges('.gold-path').forEach(edge => {
    edge.removeClass('gold-path');
    edge.removeData('hop_number');
    edge.removeData('canvas_intent');
  });
  renderTracePathsToCytoscape(cy, results.backward_trace?.paths || [], chain, 'backward_trace');
  renderTracePathsToCytoscape(cy, results.forward_trace?.paths || [], chain, 'forward_trace');


  // 5. Counterparty Label Enrichment → Update node labels and apply intents
  const cpListSync = Array.isArray(results.counterparty_triage)
    ? results.counterparty_triage
    : (results.counterparty_triage as any)?.counterparties ?? [];
  cpListSync.forEach((cp: any) => {
    const rawAddr = cp.address || cp.counterparty;
    if (!rawAddr) return;
    const addr = normalizeCanvasAddr(rawAddr);
    const node = cy.getElementById(addr);
    if (node.length) {
      if (cp.entity_name) {
        node.data('label', cp.entity_name);
        node.data('entity_name', cp.entity_name);
      }
      if (cp.entity_type) {
        node.data('entity_type', cp.entity_type);
      }
      
      const isCritical = cp.risk_tier === 'critical' || cp.risk_tier === 'severe' || cp.risk_tier === 'high' ||
                         cp.entity_type === 'exchange' || cp.entity_type === 'Exchange' || 
                         cp.entity_type === 'high_risk_exchange' || cp.entity_type === 'High_Risk_Exchange';
      
      if (isCritical) {
        applyCanvasIntent(node, CanvasNodeIntent.CRITICAL_ENDPOINT);
      } else {
        applyCanvasIntent(node, CanvasNodeIntent.HEAVY_CONTEXT);
      }
      node.addClass('top-counterparty');
    }
  });

  // ── Dual-Write Enrichment data for React state ──
  const enrichUpdates: Array<{ id: string; data: Record<string, unknown> }> = [];

  // Target node enrichments
  const targetUpdate: Record<string, unknown> = {
    canvas_intent: targetNode.data('canvas_intent')
  };
  if (results.exchange_detection?.is_exchange) {
    targetUpdate.entity_type = 'Exchange';
    targetUpdate.entity_name = results.exchange_detection.exchange_name || 'Exchange';
  }
  if (recommendation === 'ESCALATE') {
    targetUpdate.escalation_level = 'ESCALATE';
    targetUpdate.investigation_classes = ['escalate-node'];
  } else if (recommendation === 'INVESTIGATE') {
    targetUpdate.escalation_level = 'INVESTIGATE';
    targetUpdate.investigation_classes = ['investigate-node'];
  }
  if (results.velocity_profile?.classification) {
    targetUpdate.velocity_badge = getVelocityBadge(results.velocity_profile.classification);
    targetUpdate.velocity_classification = results.velocity_profile.classification;
  }
  if (Array.isArray(results.intelligence_attributions)) {
    targetUpdate.intelligence_attributions = results.intelligence_attributions;
    // R-AUDIT: High-confidence inferences elevate intent
    const best = [...results.intelligence_attributions].sort((a, b) => b.confidence - a.confidence)[0];
    if (best && best.confidence >= 0.6) {
      applyCanvasIntent(targetNode, CanvasNodeIntent.HEAVY_CONTEXT);
      targetUpdate.canvas_intent = CanvasNodeIntent.HEAVY_CONTEXT;
    }
  }
  enrichUpdates.push({ id: normalizedTarget, data: targetUpdate });

  // Counterparty enrichments
  cpListSync.forEach((cp: any) => {
    const rawAddr = cp.address || cp.counterparty;
    if (!rawAddr) return;
    const addr = normalizeCanvasAddr(rawAddr);
    const node = cy.getElementById(addr);
    if (!node.length) return;

    const cpUpdate: Record<string, unknown> = {
      canvas_intent: node.data('canvas_intent'),
      investigation_classes: ['top-counterparty']
    };
    if (cp.entity_name) {
      cpUpdate.label = cp.entity_name;
      cpUpdate.entity_name = cp.entity_name;
    }
    if (cp.entity_type) cpUpdate.entity_type = cp.entity_type;
    enrichUpdates.push({ id: addr, data: cpUpdate });
  });

  // Gold-path edge enrichment for both backward + forward trace.
  //
  // Audit: this is the only place that promotes a regular edge to
  // edgeType="gold-path" in the React store. Failure modes worth logging:
  //   (a) An edge between path[i] / path[i+1] doesn't exist on the canvas
  //       (counterparty_triage didn't surface it; SSE didn't emit it), so
  //       the gold upgrade has nothing to attach to.
  //   (b) Until 2026-05-05 we only consumed paths[0] on backward_trace and
  //       skipped forward_trace entirely — both gaps now closed.
  collectTracePathEnrichments(cy, results.backward_trace?.paths || [], 'backward_trace', enrichUpdates);
  collectTracePathEnrichments(cy, results.forward_trace?.paths || [], 'forward_trace', enrichUpdates);

  if (enrichUpdates.length > 0) {
    useGraphStore.getState().enrichElements(enrichUpdates);
  }

  console.log('✨ Canvas Sync: Complete');
}

/**
 * Get velocity badge emoji for classification
 */
function getVelocityBadge(classification: string): string {
  const badges: Record<string, string> = {
    'Relay': '⚡',
    'Distributor': '📤',
    'Accumulator': '📥',
    'Dormant': '💾',
    'Mixed': '🔀',
    'Pass-through': '⚡',
  };
  return badges[classification] || '🔍';
}

type TracePath = {
  path: string[];
  hops?: number;
  edges?: any[];
  target_entity?: { name?: string; type?: string; address?: string };
};

type TraceStep = 'backward_trace' | 'forward_trace';

/**
 * Render every trace path onto the cytoscape instance. paths[0] is treated
 * as PRIMARY_TRACE (thick amber, hop labels); paths[1..] as SECONDARY_TRACE
 * (thinner amber, no hop label rewrite). Called once per trace direction.
 *
 * Why both backward and forward share this: BFS returns multiple ranked
 * paths; until 2026-05-05 only the first was rendered as gold, the rest
 * stayed grey/secondary. Forward trace had no rendering at all.
 */
function renderTracePathsToCytoscape(
  cy: CytoscapeCore,
  paths: TracePath[],
  chain: string,
  step: TraceStep,
): void {
  if (!paths.length) return;

  // NOTE: Caller is responsible for clearing stale gold-path styling once
  // before the first render call of an investigation. Doing it here would
  // wipe what the previous direction's render just set when both backward
  // and forward run back-to-back.

  // Iterate in reverse so paths[0] (PRIMARY) is processed LAST. Cytoscape
  // applyCanvasIntent overwrites on every call, so when a hop is shared
  // between path[0] and a later path[N], the last call wins. Without
  // reversing, path[N]'s SECONDARY would clobber path[0]'s PRIMARY on
  // every shared edge — exactly what the v18 cy dump showed (every
  // backward path[0] hop ended up tagged with whichever later path
  // touched it last).
  const orderedPaths = paths.map((p, i) => ({ p, i })).reverse();
  for (const { p: pathObj, i: pathIdx } of orderedPaths) {
    const path = pathObj.path;
    if (!path || path.length < 2) continue;
    if (!path.every((addr) => cy.getElementById(normalizeCanvasAddr(addr)).length > 0)) {
      console.log(`  ${step} path[${pathIdx}]: some nodes off canvas, skipping`);
      continue;
    }

    const intent = pathIdx === 0 ? CanvasEdgeIntent.PRIMARY_TRACE : CanvasEdgeIntent.SECONDARY_TRACE;
    let hopsRendered = 0;

    for (let i = 0; i < path.length - 1; i++) {
      const source = normalizeCanvasAddr(path[i]);
      const target = normalizeCanvasAddr(path[i + 1]);

      // Trace paths are directional — fund flows source→target. Matching
      // both directions here let the v18 cy dump mark the wrong-direction
      // exposure edge (TXtR5→THMs value=0) as PRIMARY for backward path[0]
      // hop 4, when the real flow is THMs→TXtR5 with 50K USDT.
      const edges = cy.edges().filter(e => {
        const edgeSource = e.source().id();
        const edgeTarget = e.target().id();
        return edgeSource === source && edgeTarget === target;
      });

      if (edges.length > 0) {
        edges.forEach(edge => {
          applyCanvasIntent(edge, intent);
          const hopNum = i + 1;
          edge.data('hop_number', hopNum);

          // Only rewrite the label for the primary path. Secondary paths
          // keep their existing tx-aggregated label so we don't double-
          // prefix when an edge belongs to multiple paths.
          if (pathIdx === 0) {
            let baseLabel = (edge.data('label') || '').replace(/^\d+ · /, '');
            const d = edge.data();
            if (d.tx_count && (d.total_value || d.value)) {
              const val = d.total_value || d.value || 0;
              const tokens = new Map<string, number>();
              const tokenList = String(d.token_symbol || chain).split(', ').filter(Boolean);
              // `val` is ONE number (for a multi-token consolidated edge it's the
              // cross-token total). Assigning it to EVERY token inflated the label
              // ("100 USDT + 100 TRX" = 200) — R15. Attribute it to the primary
              // token only; signal the rest with a "(+N tok)" hint rather than
              // fabricating equal per-token values.
              if (tokenList.length) tokens.set(tokenList[0], val);
              baseLabel = formatConsolidatedLabel(d.tx_count, val, tokens, d.last_timestamp || d.timestamp);
              if (tokenList.length > 1) baseLabel += ` (+${tokenList.length - 1} tok)`;
            }
            edge.data('label', `${hopNum} · ${baseLabel}`);
          }
        });
        hopsRendered++;
      }

      const sNode = cy.getElementById(source);
      const tNode = cy.getElementById(target);

      sNode.data('investigation_classes', [...(sNode.data('investigation_classes') || []), 'gold-path']);
      tNode.data('investigation_classes', [...(tNode.data('investigation_classes') || []), 'gold-path']);

      // Node-intent assignment only for primary path. Secondary paths
      // shouldn't downgrade an existing TARGET_ROOT/CRITICAL_ENDPOINT to
      // HEAVY_CONTEXT just because a node sits mid-secondary-path.
      if (pathIdx === 0) {
        if (i === 0) {
          applyCanvasIntent(sNode, CanvasNodeIntent.TARGET_ROOT);
          sNode.data('is_root', true);
        }
        if (i === path.length - 2) applyCanvasIntent(tNode, CanvasNodeIntent.CRITICAL_ENDPOINT);
        else if (i > 0) applyCanvasIntent(sNode, CanvasNodeIntent.HEAVY_CONTEXT);
      }
    }

    console.log(`  ${step} path[${pathIdx}] (${intent}): ${hopsRendered}/${path.length - 1} edges${pathObj.target_entity?.name ? ` → ${pathObj.target_entity.name}` : ''}`);
  }
}

/**
 * Walk every trace path and queue React-store enrichments for each edge
 * found on the canvas. Mirrors what renderTracePathsToCytoscape does on
 * the cytoscape side; both layers must see the same paths or the canvas
 * and inspector go out of sync.
 */
function collectTracePathEnrichments(
  cy: CytoscapeCore,
  paths: TracePath[],
  step: TraceStep,
  enrichUpdates: Array<{ id: string; data: Record<string, unknown> }>,
): void {
  if (!paths.length) {
    console.debug('[canvasSync] gold_path.summary', {
      step, paths_returned: 0, paths_consumed: 0, paths_dropped: 0,
      hops_upgraded: 0, hops_missing: 0,
    });
    return;
  }

  let totalUpgraded = 0;
  let totalMissing = 0;

  // Reverse iteration so paths[0] (PRIMARY) is enqueued LAST. enrichElements
  // uses `new Map(updates)` which keeps the LAST entry per key, so for an
  // edge shared between path[0] and path[N>0] the last enrichUpdate wins.
  // Without reversing, path[N]'s SECONDARY clobbers path[0]'s PRIMARY on
  // every shared edge — the v18 cy dump showed every backward path[0] hop
  // tagged with whichever later path touched it last (pathIdx=4 instead of 0
  // on TXtR5QGh→THMsF3YY, etc.).
  const orderedPaths = paths.map((p, i) => ({ p, i })).reverse();
  for (const { p: pathObj, i: pathIdx } of orderedPaths) {
    const path = pathObj.path;
    if (!path || path.length < 2) continue;
    const pathEdges = pathObj.edges || [];
    const intent = pathIdx === 0 ? CanvasEdgeIntent.PRIMARY_TRACE : CanvasEdgeIntent.SECONDARY_TRACE;

    for (let i = 0; i < path.length - 1; i++) {
      const src = normalizeCanvasAddr(path[i]);
      const tgt = normalizeCanvasAddr(path[i + 1]);
      const edgeInfo = pathEdges[i];

      // Directional match — fund flow is src→tgt. Mirrors the directional
      // filter in renderTracePathsToCytoscape so the React-store and
      // cytoscape layers agree on which edge represents each hop.
      const edge = cy.edges().filter((e: any) => {
        const es = normalizeCanvasAddr(e.data('source') || '');
        const et = normalizeCanvasAddr(e.data('target') || '');
        return es === src && et === tgt;
      });

      if (edge.length) {
        const txsToAdd = edgeInfo?.all_transactions || (edgeInfo?.tx_hash ? [edgeInfo] : []);
        const formattedTxs = txsToAdd.map((tx: any) => ({
          tx_hash: tx.tx_hash,
          from_address: src,
          to_address: tgt,
          value: tx.value ?? 0,
          timestamp: tx.timestamp ?? null,
          token_symbol: tx.token_symbol ?? null,
          is_trace: true,
        }));

        enrichUpdates.push({
          id: edge[0].id(),
          data: {
            canvas_intent: intent,
            investigation_classes: ['gold-path'],
            edgeType: 'gold-path',
            gold_path_hop: i + 1,
            gold_path_index: pathIdx,
            gold_path_step: step,
            edge_tx_map_key: `${src}->${tgt}`,
            trace_transactions: formattedTxs,
          },
        });
        totalUpgraded += 1;
        console.debug('[canvasSync] gold_path.edge_upgrade', {
          step, pathIdx, src: src.slice(0, 18), tgt: tgt.slice(0, 18),
          hop: i + 1, edgeId: edge[0].id(),
          intent, edgeType: 'gold-path',
        });
      } else {
        totalMissing += 1;
        console.debug('[canvasSync] gold_path.edge_missing', {
          step, pathIdx, src: src.slice(0, 18), tgt: tgt.slice(0, 18),
          hop: i + 1, reason: 'no_canvas_edge_between_endpoints',
        });
      }
    }
  }

  console.debug('[canvasSync] gold_path.summary', {
    step,
    paths_returned: paths.length,
    paths_consumed: paths.length,
    paths_dropped: 0,
    hops_upgraded: totalUpgraded,
    hops_missing: totalMissing,
  });
}

/**
 * Highlight high-risk counterparty nodes, dim low-risk
 * DEPRECATED: Use Canvas Intents instead.
 */
function highlightHighRiskNodes(
  cy: CytoscapeCore,
  counterparties: Array<{ address: string; risk_tier: string }>,
  targetAddress: string
): void {
  // Replaced by tiering + Canvas Intents
}

/**
 * Reconcile edge visibility with their endpoint nodes using Canvas Intents.
 *
 * Called by AutoInvestigateCard after the SSE stream emits `complete` (and
 * after the L3 .discovering/.pruned class sweep). The L3 sweep restores
 * visibility on elements that still carry the discovering class, but it
 * misses edges that were merged into a pre-existing entry — addToCanvas
 * preserves the existing element's `_visibility` when merging, so a gold-path
 * edge that piggy-backed onto a DIMMED SSE edge stays at 0.55. This
 * reconciler is the second sweep that closes that gap.
 *
 * Rules (in priority order):
 *   1. Trace edges (PRIMARY_TRACE / DANGER_TRACE / SECONDARY_TRACE / gold-path
 *      class) — always restored to VISIBLE.
 *   2. Endpoints both AMBIENT_NOISE / orphan / pruned — PRUNED (0.15).
 *   3. Endpoints both bfs-explored — SECONDARY (0.8).
 *   4. Anything still parked at DIMMED with a non-noise endpoint — VISIBLE.
 *      Catches the merge-into-DIMMED race the L3 sweep misses.
 *   5. Default — VISIBLE.
 */
export function reconcileEdgeVisibility(cy: CytoscapeCore): void {
  cy.batch(() => {
    cy.edges().forEach((edge: any) => {
      const src = edge.source();
      const tgt = edge.target();
      const intent = edge.data('canvas_intent');

      // Trace edges: always fully visible. Covers primary (gold path),
      // danger trace, and any explicit secondary trace too.
      const isTrace =
        intent === CanvasEdgeIntent.PRIMARY_TRACE ||
        intent === CanvasEdgeIntent.DANGER_TRACE ||
        edge.hasClass('gold-path') ||
        edge.data('edgeType') === 'gold-path';
      if (isTrace) {
        edge.data('_visibility', VISIBILITY.VISIBLE);
        return;
      }

      // Both endpoints orphan or pruned → edge is ambient noise
      const srcAmbient = src.hasClass('orphan-node') || src.hasClass('pruned') || src.data('canvas_intent') === CanvasNodeIntent.AMBIENT_NOISE;
      const tgtAmbient = tgt.hasClass('orphan-node') || tgt.hasClass('pruned') || tgt.data('canvas_intent') === CanvasNodeIntent.AMBIENT_NOISE;

      if (srcAmbient || tgtAmbient) {
        applyCanvasIntent(edge, CanvasEdgeIntent.AMBIENT_NOISE);
        edge.data('_visibility', VISIBILITY.PRUNED);
        return;
      }

      // Both endpoints are bfs-explored → secondary trace
      const srcBfs = src.hasClass('bfs-explored');
      const tgtBfs = tgt.hasClass('bfs-explored');
      if (srcBfs && tgtBfs) {
        applyCanvasIntent(edge, CanvasEdgeIntent.SECONDARY_TRACE);
        edge.data('_visibility', VISIBILITY.SECONDARY);
        return;
      }

      // Stuck at DIMMED with a non-noise endpoint — restore to VISIBLE.
      // This is the merge-into-DIMMED race: an SSE edge_discovered event
      // landed at _visibility=DIMMED, then a later edge for the same pair
      // (e.g. trace gold-path) was merged into it. addToCanvas keeps the
      // existing _visibility on merge, so the merged edge is also stuck
      // dimmed even though the trace step has finished.
      const vis = edge.data('_visibility');
      if (typeof vis === 'number' && vis <= VISIBILITY.DIMMED + 1e-3) {
        edge.data('_visibility', VISIBILITY.VISIBLE);
        return;
      }

      // Default: standard
      applyCanvasIntent(edge, CanvasEdgeIntent.STANDARD);
      edge.data('_visibility', VISIBILITY.VISIBLE);
    });
  });
}

/**
 * Clear all investigation-related visual highlighting
 */
export function clearInvestigationHighlighting(cy: CytoscapeCore): void {
  console.log('🧹 Canvas: Clearing investigation highlighting');

  cy.batch(() => {
    cy.elements().forEach(ele => {
      ele.removeData('canvas_intent');
      ele.removeData('hop_number');
      ele.removeClass('gold-path top-counterparty bfs-explored orphan-node discovering');
    });
  });

  console.log('✓ Canvas: Cleared Gold Path and highlighting');
}

/**
 * Clear all investigation styling including escalation markers
 */
export function clearAllInvestigationStyling(cy: CytoscapeCore): void {
  console.log('🧹 Canvas: Clearing ALL investigation styling');

  clearInvestigationHighlighting(cy);

  cy.batch(() => {
    cy.elements().forEach(ele => {
      ele.removeData('canvas_intent');
      ele.removeData('is_root');
      ele.removeData('escalation_level');
      ele.removeData('velocity_badge');
      ele.removeData('velocity_classification');
      ele.removeClass('escalate-node investigate-node pruned follow-money-highlight');
    });
  });

  console.log('✓ Canvas: Cleared all investigation styling');
}

/**
 * Entity types that qualify a node as Tier 1 (always visible).
 *
 * Package 8: membership is decided by one normalised check
 * (`isTier1EntityType` in canvasAssurance.ts), so every casing and the
 * backend categories (darknet_market, phishing, fraud, scam, p2p, mixer ...)
 * match. This object keeps the `.has()` call shape for existing callers but
 * normalises its argument; do not extend it with literals, extend
 * TIER1_ENTITY_KEYS.
 */
export const TIER1_ENTITY_TYPES = {
  has: (t: unknown): boolean => isTier1EntityType(t),
};

/**
 * Classify every node on canvas into intelligence tiers and assign Canvas Intents.
 */
export function applyCanvasTiers(cy: CytoscapeCore, rootAddress: string): void {
  const normalizedRoot = normalizeCanvasAddr(rootAddress);
  let t1 = 0, t2 = 0, t3 = 0;

  // Collect gold-path node IDs
  const goldPathNodes = new Set<string>();
  cy.edges(`[canvas_intent = "${CanvasEdgeIntent.PRIMARY_TRACE}"]`).forEach((edge: any) => {
    goldPathNodes.add(edge.source().id());
    goldPathNodes.add(edge.target().id());
  });

  // Collect direct neighbors of root (hop-1)
  const rootNode = cy.getElementById(normalizedRoot);
  const hop1Neighbors = new Set<string>();
  if (rootNode.length) {
    rootNode.connectedEdges().forEach((edge: any) => {
      hop1Neighbors.add(edge.source().id());
      hop1Neighbors.add(edge.target().id());
    });
    hop1Neighbors.delete(normalizedRoot);
  }

  cy.nodes().forEach((node: any) => {
    const id = node.id();
    const d = node.data();
    let intent: CanvasNodeIntent = CanvasNodeIntent.STANDARD;
    let tier = 3;

    if (id === normalizedRoot) {
      intent = CanvasNodeIntent.TARGET_ROOT;
      tier = 1;
    } else if (d.entity_type && TIER1_ENTITY_TYPES.has(d.entity_type)) {
      intent = CanvasNodeIntent.CRITICAL_ENDPOINT;
      tier = 1;
    } else if (goldPathNodes.has(id)) {
      intent = CanvasNodeIntent.HEAVY_CONTEXT;
      tier = 1;
    } else if (node.hasClass('top-counterparty')) {
      intent = CanvasNodeIntent.HEAVY_CONTEXT;
      tier = 1;
    } else if (node.hasClass('escalate-node') || node.hasClass('investigate-node')) {
      intent = CanvasNodeIntent.CRITICAL_ENDPOINT;
      tier = 1;
    } else if (Array.isArray(d.intelligence_attributions) && d.intelligence_attributions.some((a: any) => (a.confidence || 0) >= 0.6)) {
      // R-CANVAS-INTENT: Elevate probabilistic identities to Tier 1
      intent = CanvasNodeIntent.HEAVY_CONTEXT;
      tier = 1;
    } else if (hop1Neighbors.has(id)) {
      const hasValue = node.connectedEdges().some((edge: any) => {
        const otherEnd = edge.source().id() === id ? edge.target().id() : edge.source().id();
        return otherEnd === normalizedRoot && edge.data('value') != null && Number(edge.data('value')) > 0;
      });
      if (hasValue) {
        intent = CanvasNodeIntent.HEAVY_CONTEXT;
        tier = 2;
      }
    }

    if (tier === 3 && (node.hasClass('pruned') || node.hasClass('orphan-node'))) {
      intent = CanvasNodeIntent.AMBIENT_NOISE;
    }

    applyCanvasIntent(node, intent);
    // Ambient noise is dimmed through the single opacity channel (R11), not a
    // stylesheet opacity on the intent. Leaving the intent restores VISIBLE
    // only when this sweep was what dimmed the node.
    if (intent === CanvasNodeIntent.AMBIENT_NOISE) {
      node.data('_visibility', VISIBILITY.PRUNED);
    } else if (node.data('_visibility') === VISIBILITY.PRUNED && !node.hasClass('pruned')) {
      node.data('_visibility', VISIBILITY.VISIBLE);
    }
    node.data('canvas_tier', tier);
    
    if (tier === 1) t1++;
    else if (tier === 2) t2++;
    else t3++;
  });

  console.log(`✓ Canvas: Tiered nodes — T1: ${t1}, T2: ${t2}, T3: ${t3}`);
}

/**
 * Compute a human-readable edge label from value/token data.
 * Replicates the SSE edge_discovered label format: "1234.56 USDT (15 txs)"
 */
export function computeEdgeLabel(value: number | string | null | undefined, tokenSymbol?: string, txCount?: number): string {
  let label = '';
  if (value != null && value !== 0 && value !== '') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (!isNaN(num) && num !== 0) {
      const val = num.toFixed(2);
      const tok = cleanTokenSymbol(tokenSymbol);
      label = tok ? `${val} ${tok}` : val;
    }
  }
  if (txCount && txCount > 1) {
    label += label ? ` (${txCount} txs)` : `${txCount} txs`;
  }
  return label;
}

// ────────────────────────────────────────────────────────────────────────
// Unified Canvas Write Boundary
// ────────────────────────────────────────────────────────────────────────

/** Spam token symbols — must stay in sync with backend spam_tokens.py */
const _SPAM_TOKEN_SYMBOLS = new Set([
  'UNKNOWN', 'Rocky', 'foxupay', 'OSEToken',
  'HX28com', 'HX28.com', 'APENFT', 'SunSwap',
]);

const _SPAM_VALUE_THRESHOLD = 1e12;

/**
 * Normalize entity_type to the canonical casing used by Cytoscape style
 * rules. Maps Arkham's "labeled" to a meaningful type by inspecting
 * entity_name when possible.
 */
export function normalizeEntityType(
  entityType: string | undefined | null,
  entityName: string | undefined | null,
): string | undefined {
  if (!entityType) return entityType ?? undefined;

  const lower = entityType.toLowerCase();

  // If "labeled" or "other", infer from entity_name keywords
  if (lower === 'labeled' || lower === 'other') {
    if (entityName) {
      // Single vocabulary shared with the backend (constants/entityKeywords.ts):
      // a mixer or no-KYC swap service named "... Deposit" is not an Exchange.
      if (isExchangeByName(entityName)) {
        return 'Exchange';
      }
    }
    // Can't infer — keep as-is but title-case for consistency
    return entityType;
  }

  // Canonical casings that match Cytoscape style selectors in GraphCanvas.tsx
  const canonicalMap: Record<string, string> = {
    exchange: 'Exchange',
    mixer: 'Mixer',
    defi: 'DeFi',
    service: 'Service',
    p2p: 'P2P',
    smartcontract: 'SmartContract',
    mule: 'Mule',
    bridge: 'Bridge',
    hacker: 'Hacker',
    notable: 'Notable',
    scam: 'Scam',
    sanctioned: 'Sanctioned',
    ransomware: 'Ransomware',
    darknet: 'Darknet',
    exploit: 'Exploit',
    high_risk_exchange: 'High_Risk_Exchange',
  };

  return canonicalMap[lower] ?? entityType;
}

function _isSpamEdge(tokenSymbol: string | null | undefined, value: number | null | undefined): boolean {
  if (tokenSymbol && _SPAM_TOKEN_SYMBOLS.has(tokenSymbol)) return true;
  if (value != null) {
    try { if (Number(value) >= _SPAM_VALUE_THRESHOLD) return true; } catch { /* noop */ }
  }
  return false;
}

export interface AddToCanvasOptions {
  /** Chain context (e.g. "TRX", "BTC"). Applied to nodes missing chain data. */
  chain?: string;
  /** CSS classes to add to ALL elements in this batch (e.g. "discovering"). */
  classes?: string;
  /** When true, edges with edgeType "gold-path" get the .gold-path CSS class. */
  applyGoldPathClass?: boolean;
  /** When true, spam token edges are silently dropped. Default: true */
  filterSpam?: boolean;
}

/**
 * Unified canvas write boundary.  ALL code paths that add nodes/edges
 * to the Cytoscape canvas should call this function instead of raw
 * `setElements(prev => [...prev, ...raw])`.
 *
 * Responsibilities:
 *  1. Normalizes addresses (EVM lowercase, TRX/BTC preserved)
 *  2. Normalizes entity_type to canonical Cytoscape casing
 *  3. Ensures chain is populated from context
 *  4. Applies .gold-path CSS class for gold-path edges
 *  5. Deduplicates against existing canvas elements
 *  6. Filters spam token edges
 *  7. Upgrades existing edges to gold-path instead of creating duplicates
 *  8. Promotes placement_role monotonically per CANVAS_PLACEMENT_LANGUAGE §4.3
 *
 * Returns the new elements array to pass to `setElements()`.
 */
export function addToCanvas(
  prevElements: any[],
  newElements: any[],
  options: AddToCanvasOptions = {},
): any[] {
  const {
    chain,
    classes,
    applyGoldPathClass = true,
    filterSpam = false,  // R-AUDIT: Default OFF — show all edges for data sanctity. Users filter via GraphFilterBar.
  } = options;

  if (!newElements || newElements.length === 0) return prevElements;

  let merged = [...prevElements];
  const existingIds = new Set(merged.map(e => e.data.id));
  
  // R-AUDIT: Use strict directional keys (source->target) for edge deduplication.
  // Forensic transactions are directed; A->B and B->A must remain distinct.
  const edgeByPair = new Map<string, number>();
  merged.forEach((el, idx) => {
    if (el.data.source && el.data.target) {
      edgeByPair.set(`${el.data.source}->${el.data.target}`, idx);
    }
  });

  // Track node IDs (existing + newly added) for edge endpoint validation.
  // Index both raw and lowercase so checksum vs lower EVM ids still match.
  const allNodeIds = new Set<string>();
  const rememberNode = (id: string) => {
    if (!id) return;
    allNodeIds.add(id);
    allNodeIds.add(id.toLowerCase());
  };
  merged.filter(e => !e.data.source).forEach(e => rememberNode(e.data.id));

  // Separate nodes and edges, process nodes first
  const nodes = newElements.filter(e => !e.data?.source);
  const edges = newElements.filter(e => !!e.data?.source);

  // ── Process nodes ──
  for (const el of nodes) {
    const d = { ...el.data };

    // 1. Address normalization
    d.id = normalizeCanvasAddr(d.id);
    
    // If node exists, enrich its data rather than skipping
    const existingNodeIdx = merged.findIndex(e => !e.data.source && e.data.id === d.id);
    if (existingNodeIdx !== -1) {
      const existingData = merged[existingNodeIdx].data;
      merged[existingNodeIdx] = {
        ...merged[existingNodeIdx],
        data: {
          ...existingData,
          ...d,
          // Preserve existing intent if new one is standard
          canvas_intent: d.canvas_intent && d.canvas_intent !== CanvasNodeIntent.STANDARD
            ? d.canvas_intent
            : existingData.canvas_intent,
          // Monotonic role precedence — the higher role wins, never demotes.
          // CANVAS_PLACEMENT_LANGUAGE §4.3.
          placement_role: pickHigherRole(existingData.placement_role, d.placement_role),
        }
      };
      continue;
    }

    // 2. Entity type normalization
    d.entity_type = normalizeEntityType(d.entity_type, d.entity_name ?? d.label);

    // 3. Chain population
    if (!d.chain && chain) d.chain = chain;

    // 4. Default placement_role when producer omitted it (layout treats as context).
    if (!d.placement_role) d.placement_role = 'context';

    // Build CSS classes
    let cls = el.classes || '';
    if (classes) cls = cls ? `${cls} ${classes}` : classes;

    merged.push({ data: d, ...(cls ? { classes: cls } : {}) });
    existingIds.add(d.id);
    rememberNode(d.id);
  }

  // ── Process edges ──
  for (const el of edges) {
    const d = { ...el.data };

    // 1. Address normalization on endpoints
    d.source = normalizeCanvasAddr(d.source);
    d.target = normalizeCanvasAddr(d.target);

    // 2. Filter out zero-value edges that don't add info (industry standard de-noising)
    // We allow 0-value edges only if they are explicitly part of a trace/gold-path
    // OR if filterSpam is false (Forensic Sanctity mode).
    const val = Number(d.value || d.total_value || 0);
    const isTrace = d.edgeType === 'gold-path' || d.edgeType === 'inferred' || !!d.trace_transactions;
    if (filterSpam && val === 0 && !isTrace && (d.tx_count || 1) <= 1) continue;
    
    // 3. Spam filter (token-based)
    if (filterSpam && _isSpamEdge(d.token_symbol, d.value)) continue;

    // 4. Edge dedup — strict directional match
    const dirKey = `${d.source}->${d.target}`;
    const existingIdx = edgeByPair.get(dirKey);
    
    if (existingIdx !== undefined) {
      const existing = merged[existingIdx];
      const isGoldPath = (existing.data.edgeType === 'gold-path' || d.edgeType === 'gold-path');

      // Visibility on merge: take the MAX. The same pair can arrive both as
      // a gold-path edge (_visibility=1.0) and as an SSE discovering edge
      // (_visibility=0.55). A naive `...d` spread would let the later one
      // win, so a gold-path that lands first then gets re-touched by SSE
      // would silently downgrade to DIMMED. Always promote to the higher
      // visibility — trace status is monotonic, never demoted by enrichment.
      const oldVis = typeof existing.data._visibility === 'number' ? existing.data._visibility : VISIBILITY.VISIBLE;
      const newVisIn = typeof d._visibility === 'number' ? d._visibility : oldVis;
      const mergedVis = Math.max(oldVis, newVisIn);

      // Update existing edge data with new info (enrichment)
      merged[existingIdx] = {
        ...existing,
        data: {
          ...existing.data,
          ...d,
          // R-AUDIT: Always MAX — the same edge pair may arrive from different
          // code paths (tx-search, expand, SSE) representing the SAME transaction.
          // SUM would double-count. MAX preserves the highest observed value.
          value: (() => {
            const oldVal = Number(existing.data.value || 0);
            const newVal = Math.max(oldVal, val);
            // Pipeline recording: track edge value changes
            if (oldVal !== newVal && oldVal > 0) {
              try {
                recordEdgeValueChange(dirKey, oldVal, newVal, 'addToCanvas.merge');
              } catch { /* recording must never break canvas merge */ }
            }
            return newVal;
          })(),
          tx_count: Math.max(existing.data.tx_count || 1, d.tx_count || 1),
          // Upgrade to gold-path if either is gold-path
          edgeType: isGoldPath ? 'gold-path' : (existing.data.edgeType || d.edgeType),
          canvas_intent: d.canvas_intent && d.canvas_intent !== CanvasEdgeIntent.STANDARD
            ? d.canvas_intent
            : existing.data.canvas_intent,
          _visibility: mergedVis,
        },
        classes: isGoldPath && applyGoldPathClass
          ? ((existing.classes || '') + ' gold-path').trim()
          : existing.classes,
      };
      continue;
    }

    // 4. Strictly use source->target as ID for non-gold consolidated edges
    // This prevents ghost edges where multiple IDs refer to the same pair.
    if (!d.edgeType || d.edgeType !== 'gold-path') {
        d.id = dirKey;
    }

    // Skip if exact ID already exists
    if (d.id && existingIds.has(d.id)) continue;

    // 4. Only add edge if both endpoints exist
    if (!allNodeIds.has(d.source) || !allNodeIds.has(d.target)) continue;

    // 5. Build classes
    let cls = el.classes || '';
    if (classes) cls = cls ? `${cls} ${classes}` : classes;
    if (applyGoldPathClass && d.edgeType === 'gold-path') {
      if (!cls.includes('gold-path')) cls = cls ? `${cls} gold-path` : 'gold-path';
    }

    merged.push({ data: d, ...(cls ? { classes: cls } : {}) });
    if (d.id) {
      existingIds.add(d.id);
    }
    edgeByPair.set(dirKey, merged.length - 1);
  }

  // ── Prune orphan nodes introduced in this batch whose edges were dropped/filtered ──
  // Fixes Thread 2 UX gap: hanging nodes on canvas with no incident edges.
  if (filterSpam && edges.length > 0) {
    const connectedNodeIds = new Set<string>();
    for (const el of merged) {
      if (el.data?.source && el.data?.target) {
        // R1: chain-aware canon (EVM 0x → lowercase; TRX/BTC/SOL case preserved).
        connectedNodeIds.add(el.data.source);
        connectedNodeIds.add(el.data.target);
        connectedNodeIds.add(normalizeCanvasAddr(el.data.source));
        connectedNodeIds.add(normalizeCanvasAddr(el.data.target));
      }
    }
    const prevNodeIdSet = new Set(prevElements.filter((p: any) => !p.data?.source).map((p: any) => p.data?.id));
    merged = merged.filter((el: any) => {
      if (el.data?.source) return true; // Edge
      const id = el.data?.id;
      if (!id) return true;
      if (connectedNodeIds.has(id) || connectedNodeIds.has(normalizeCanvasAddr(id))) return true;
      // Keep root, anchors, or nodes already present on canvas prior to this addition
      if (el.data?.isRoot || el.data?.is_root || el.data?.placement_role === 'root' || el.classes?.includes('cs-root')) return true;
      return prevNodeIdSet.has(id);
    });
  }

  // Pipeline recording: track what was added/merged
  try {
    const beforeNodes = prevElements.filter((e: any) => !e.data?.source).length;
    const beforeEdges = prevElements.filter((e: any) => e.data?.source).length;
    const afterNodes = merged.filter((e: any) => !e.data?.source).length;
    const afterEdges = merged.filter((e: any) => e.data?.source).length;
    const inputNodes = newElements.filter((e: any) => !e.data?.source).length;
    const inputEdges = newElements.filter((e: any) => e.data?.source).length;
    recordGraphMutation(
      'addToCanvas',
      { nodes: beforeNodes, edges: beforeEdges },
      { nodes: afterNodes, edges: afterEdges },
      { nodes: inputNodes, edges: inputEdges },
      `input=${newElements.length} merged=${merged.length - prevElements.length} chain=${chain || '?'}`
    );
  } catch { /* recording must never break the canvas merge */ }

  return merged;
}

// ────────────────────────────────────────────────────────────────────────
// Edge backfill — fill direct on-chain edges between on-canvas nodes that
// no individual algorithm explicitly emitted.
// ────────────────────────────────────────────────────────────────────────

/**
 * Fetch all direct on-chain edges between *addresses* on the given chain.
 * Returns Cytoscape elements ready to be passed through `addToCanvas`.
 *
 * Closes the architectural gap where each algorithm (Links / autoTrace /
 * AutoInvestigate SSE) only emits the edges it personally discovered —
 * leaving inter-pair edges between independently-added nodes invisible.
 *
 * Mirrors `enrichExpandedTransactions` in spirit: a backend round-trip that
 * fills a layer of canvas data the per-algorithm code paths don't cover.
 */
export async function fetchEdgesBetween(
  chain: string,
  addresses: string[],
  limit: number = 500,
): Promise<any[]> {
  if (!chain || addresses.length < 2) return [];
  try {
    const edges = await addressService.edgesBetween(chain, addresses, limit);
    return edges.map((e) => ({
      data: {
        source: e.from_address,
        target: e.to_address,
        value: e.value ?? 0,
        tx_hash: e.tx_hash,
        timestamp: e.timestamp,
        token_symbol: e.token_symbol,
        edgeType: 'transaction',
        _visibility: 1.0,
        label: computeEdgeLabel(e.value ?? null, e.token_symbol ?? undefined),
      },
    }));
  } catch (err) {
    console.warn('[canvasSync] fetchEdgesBetween failed', err);
    return [];
  }
}

// Module-level debounce so rapid SSE-driven addNodesToCanvas() calls
// coalesce into a single backfill round per chain.
let _backfillTimer: ReturnType<typeof setTimeout> | null = null;
const _backfillChainsPending = new Set<string>();

/**
 * Schedule a debounced edge backfill for the given chain. Coalesces calls
 * within 600ms (chosen to comfortably outlive the SSE 200ms flush window).
 *
 * Pass `getAddressesForChain` so the backfill always reads the *current*
 * canvas state at fire-time (not a stale closure of in-flight additions).
 * `onElementsReady` receives Cytoscape elements; pass them to `addToCanvas`.
 */
export function scheduleEdgeBackfill(
  chain: string,
  getAddressesForChain: (ch: string) => string[],
  onElementsReady: (elements: any[]) => void,
  delayMs: number = 600,
): void {
  if (!chain) return;
  _backfillChainsPending.add(chain);
  if (_backfillTimer) clearTimeout(_backfillTimer);
  _backfillTimer = setTimeout(async () => {
    _backfillTimer = null;
    const chains = Array.from(_backfillChainsPending);
    _backfillChainsPending.clear();
    for (const ch of chains) {
      const addrs = getAddressesForChain(ch);
      if (addrs.length < 2) continue;
      const elements = await fetchEdgesBetween(ch, addrs);
      if (elements.length > 0) onElementsReady(elements);
    }
  }, delayMs);
}
